import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import GenericDataGrid from '../../../components/genericTable';
import Loader from '../../../components/loader';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import MobileLoginCodeDialog from '../../../components/mobileLoginCodeDialog';
import { FilterSearchField } from '../../../components/filterControls';
import { createManagementTableSx } from '../../../styles/tableStyles';
import { compactTableActionButtonSx } from '../../../styles/actionStyles';
import { listMobileAgents, listMobileFarmers, mobileLoginCodeApi } from './mobileUsers.service';

const dataGridSx = createManagementTableSx({
  headerHeight: 44,
  headerFontSize: 16,
  cellPaddingX: 12,
  hideSelectedRowCount: true,
});

const TABS = [
  { label: 'Agents', fetch: listMobileAgents },
  { label: 'Farmers', fetch: listMobileFarmers },
];

const textCell = (value) => (
  <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
    {value || '-'}
  </Typography>
);

export default function MobileUsers() {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [mobileCode, setMobileCode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await TABS[tab].fetch(page, pageSize, search);
    setLoading(false);
    if (res.statusCode === 200 && res.data) {
      setRows((res.data.content || []).map((u) => ({ id: u.userId, ...u })));
      setTotalCount(res.data.totalElements || 0);
    } else {
      setRows([]);
      setTotalCount(0);
      setError(res.message || 'Failed to load data');
    }
  }, [tab, page, pageSize, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (_e, next) => {
    setTab(next);
    setPage(0);
    setSearchInput('');
    setSearch('');
    setRows([]);
  };

  // Cache the code issued for each user so reopening the dialog shows the SAME code until
  // it expires — a new API call would invalidate the still-valid code server-side.
  const codeCacheRef = useRef({});

  const handleSendCode = async (userId) => {
    const cached = codeCacheRef.current[userId];
    if (cached?.expiresAt && new Date(cached.expiresAt).getTime() > Date.now()) {
      setMobileCode(cached);
      return;
    }
    setSendingId(userId);
    const res = await mobileLoginCodeApi(userId);
    setSendingId(null);
    if (res?.statusCode === 200 && res.data?.code) {
      codeCacheRef.current[userId] = res.data;
      setMobileCode(res.data);
    } else {
      setSnackbar({ open: true, message: res?.message || 'Unable to generate a mobile login code', severity: 'error' });
    }
  };

  const columns = useMemo(() => [
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => textCell(params.value),
    },
    {
      field: 'dareName',
      headerName: 'DARE',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => textCell(params.value),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const phone = params.value ? String(params.value).trim() : '';
        const code = params.row?.countryCode ? String(params.row.countryCode).trim() : '';
        let display = '-';
        if (phone) display = code && !phone.startsWith('+') ? `${code} ${phone}` : phone;
        return textCell(display);
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 220,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleSendCode(params.row.id)}
          disabled={sendingId === params.row.id}
          startIcon={<Icon icon="material-symbols:smartphone-outline-rounded" width="18" height="18" />}
          sx={{ ...compactTableActionButtonSx, backgroundColor: '#E6F4EA', color: '#1B5E20', '&:hover': { backgroundColor: '#D6EBDA' } }}
        >
          {sendingId === params.row.id ? 'Generating…' : 'Send OTP code'}
        </Button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [sendingId]);

  return (
    <>
      <Typography
        className="app-page-title"
        sx={{ fontSize: '34px', lineHeight: '40px', letterSpacing: '-0.25px', fontWeight: 700, color: '#1C1B1F', mb: 1 }}
      >
        Mobile Users
      </Typography>
      <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', mb: 3 }}>
        Agents and Farmers who log in on the mobile apps. Generate a one-time login code to sign in as any of them for support.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 16 } }}>
          {TABS.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
        <FilterSearchField
          placeholder="Search name or phone"
          value={searchInput}
          onChange={setSearchInput}
          testId="mobile-users-search"
          // Sits opposite the tabs rather than on a filter row of its own, so it holds its width
          // instead of stretching across the gap between them.
          sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid
          rows={rows}
          columns={columns}
          rowCount={totalCount}
          page={page}
          pageSize={pageSize}
          loading={loading}
          errors={error}
          dataGridSx={dataGridSx}
          autoHeight
          rowHeight={60}
          onPaginationChange={(nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          }}
        />
      </Box>

      <MobileLoginCodeDialog
        codeData={mobileCode}
        onClose={() => setMobileCode(null)}
        onCopied={() => setSnackbar({ open: true, message: 'Code copied', severity: 'success' })}
      />

      <NotificationSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {loading && <Loader />}
    </>
  );
}
